import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CalendarClock,
  CheckCircle2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Save,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  RichTextEditor,
  type RichTextEditorValue,
} from "@/components/shared/RichTextEditor";
import { BlogPreviewDialog } from "@/components/blog/BlogPreviewDialog";
import { GenerateDraftDialog } from "@/components/blog/GenerateDraftDialog";
import { GenerateCoverImageDialog } from "@/components/blog/GenerateCoverImageDialog";
import { slugify } from "@/lib/slugify";
import {
  createPost,
  deletePost,
  getAdminPost,
  generateBlogCoverImage,
  publishPost,
  schedulePost,
  unpublishPost,
  updatePost,
  uploadBlogImage,
  type BlogPostAdmin,
  type BlogPostStatus,
  type CreateBlogPostInput,
} from "@/services/blogService";

const BLOG_POST_QUERY_KEY = "blog-post";

const COVER_ALLOWED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const COVER_MAX_BYTES = 5 * 1024 * 1024;

function statusVariant(
  status: BlogPostStatus,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "published":
      return "default";
    case "scheduled":
      return "secondary";
    case "draft":
      return "outline";
    case "archived":
      return "destructive";
  }
}

function parseTagsInput(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

interface EditorFormState {
  title: string;
  slug: string;
  slugTouched: boolean;
  excerpt: string;
  content: RichTextEditorValue;
  coverImageUrl: string;
  coverImageAlt: string;
  tagsInput: string;
  seoTitle: string;
  seoDescription: string;
  ogImageUrl: string;
  scheduledFor: string;
}

const emptyForm: EditorFormState = {
  title: "",
  slug: "",
  slugTouched: false,
  excerpt: "",
  content: { html: "", json: {} },
  coverImageUrl: "",
  coverImageAlt: "",
  tagsInput: "",
  seoTitle: "",
  seoDescription: "",
  ogImageUrl: "",
  scheduledFor: "",
};

function stateFromPost(post: BlogPostAdmin): EditorFormState {
  return {
    title: post.title,
    slug: post.slug,
    slugTouched: true,
    excerpt: post.excerpt ?? "",
    content: { html: post.contentHtml, json: post.contentJson },
    coverImageUrl: post.coverImageUrl ?? "",
    coverImageAlt: post.coverImageAlt ?? "",
    tagsInput: post.tags.map((tag) => tag.name).join(", "),
    seoTitle: post.seoTitle ?? "",
    seoDescription: post.seoDescription ?? "",
    ogImageUrl: post.ogImageUrl ?? "",
    scheduledFor: post.scheduledFor
      ? toLocalDateTimeInput(post.scheduledFor)
      : "",
  };
}

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDateTimeInput(local: string): string {
  return new Date(local).toISOString();
}

function formToCreateInput(form: EditorFormState): CreateBlogPostInput {
  return {
    title: form.title.trim(),
    slug: form.slug.trim() || undefined,
    excerpt: form.excerpt.trim() || undefined,
    contentHtml: form.content.html,
    contentJson: form.content.json,
    coverImageUrl: form.coverImageUrl.trim() || undefined,
    coverImageAlt: form.coverImageAlt.trim() || undefined,
    seoTitle: form.seoTitle.trim() || undefined,
    seoDescription: form.seoDescription.trim() || undefined,
    ogImageUrl: form.ogImageUrl.trim() || undefined,
    tagSlugs: parseTagsInput(form.tagsInput),
  };
}

export function BlogEditorPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === "new";

  const [form, setForm] = useState<EditorFormState>(emptyForm);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [autoCoverPending, setAutoCoverPending] = useState(false);
  const [coverLoadError, setCoverLoadError] = useState(false);

  useEffect(() => {
    setCoverLoadError(false);
  }, [form.coverImageUrl]);

  const { data: post, isLoading: isLoadingPost } = useQuery({
    queryKey: [BLOG_POST_QUERY_KEY, id],
    queryFn: () => getAdminPost(id!),
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (post) setForm(stateFromPost(post));
  }, [post]);

  const saveMutation = useMutation({
    mutationFn: async (state: EditorFormState) => {
      const input = formToCreateInput(state);
      if (isNew) {
        return createPost(input);
      }
      return updatePost(id!, input);
    },
    onSuccess: (saved) => {
      toast.success(t("blog.editor.toast.saved"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "blog"] });
      if (isNew) {
        navigate(`/platform/blog/${saved.id}`, { replace: true });
      } else {
        queryClient.setQueryData([BLOG_POST_QUERY_KEY, saved.id], saved);
      }
    },
    onError: () => toast.error(t("blog.editor.toast.saveError")),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      const input = formToCreateInput(form);
      const saved = isNew
        ? await createPost(input)
        : await updatePost(id!, input);
      return publishPost(saved.id);
    },
    onSuccess: (published) => {
      toast.success(t("blog.editor.toast.published"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "blog"] });
      navigate(`/platform/blog/${published.id}`, { replace: true });
    },
    onError: () => toast.error(t("blog.editor.toast.publishError")),
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishPost(id!),
    onSuccess: (updated) => {
      toast.success(t("blog.editor.toast.unpublished"));
      queryClient.setQueryData([BLOG_POST_QUERY_KEY, updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ["platform", "blog"] });
    },
    onError: () => toast.error(t("blog.editor.toast.unpublishError")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePost(id!),
    onSuccess: () => {
      toast.success(t("blog.editor.toast.deleted"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "blog"] });
      setDeleteConfirmOpen(false);
      navigate("/platform/blog", { replace: true });
    },
    onError: () => toast.error(t("blog.editor.toast.deleteError")),
  });

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!form.scheduledFor) throw new Error("scheduledFor required");
      const input = formToCreateInput(form);
      const saved = isNew
        ? await createPost(input)
        : await updatePost(id!, input);
      return schedulePost(saved.id, fromLocalDateTimeInput(form.scheduledFor));
    },
    onSuccess: (scheduled) => {
      toast.success(t("blog.editor.toast.scheduled"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "blog"] });
      navigate(`/platform/blog/${scheduled.id}`, { replace: true });
    },
    onError: () => toast.error(t("blog.editor.toast.scheduleError")),
  });

  const updateField = useCallback(
    <K extends keyof EditorFormState>(key: K, value: EditorFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => {
        const nextTitle = e.target.value;
        const nextSlug = prev.slugTouched ? prev.slug : slugify(nextTitle);
        return { ...prev, title: nextTitle, slug: nextSlug };
      });
    },
    [],
  );

  const handleSlugChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }));
    },
    [],
  );

  const handleCoverChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (!COVER_ALLOWED_MIME.has(file.type)) {
        toast.error(t("blog.editor.toast.imageType"));
        return;
      }
      if (file.size > COVER_MAX_BYTES) {
        toast.error(t("blog.editor.toast.imageTooLarge"));
        return;
      }
      setUploadingCover(true);
      try {
        const { url } = await uploadBlogImage(file);
        setForm((prev) => ({ ...prev, coverImageUrl: url }));
        toast.success(t("blog.editor.toast.imageUploaded"));
      } catch {
        toast.error(t("blog.editor.toast.imageUploadError"));
      } finally {
        setUploadingCover(false);
      }
    },
    [t],
  );

  const hasContent =
    form.title.trim().length > 0 ||
    form.excerpt.trim().length > 0 ||
    form.content.html.replace(/<[^>]+>/g, "").trim().length > 0;

  const isPublished = post?.status === "published";
  const isScheduled = post?.status === "scheduled";
  const canSubmit =
    form.title.trim().length > 0 && form.content.html.trim().length > 0;
  const isBusy =
    saveMutation.isPending ||
    publishMutation.isPending ||
    scheduleMutation.isPending ||
    unpublishMutation.isPending ||
    deleteMutation.isPending;

  const statusLabel = useMemo(() => {
    if (isNew) return t("blog.list.status.draft");
    if (!post) return "";
    return t(`blog.list.status.${post.status}`);
  }, [isNew, post, t]);

  if (!isNew && isLoadingPost) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-8">
      <PageHeader
        title={isNew ? t("blog.editor.titleNew") : t("blog.editor.titleEdit")}
        description={t("blog.editor.description")}
        onBack={() => navigate("/platform/blog")}
        actions={
          <div className="flex items-center gap-2">
            {(isPublished || isScheduled) && (
              <Badge variant={statusVariant(post!.status)}>{statusLabel}</Badge>
            )}
            <Button
              variant="outline"
              onClick={() => setDraftDialogOpen(true)}
              disabled={isBusy}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {t("blog.editor.actions.generateWithAi")}
            </Button>
            <Button
              variant="outline"
              disabled={!canSubmit || isBusy}
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="mr-2 h-4 w-4" />
              {t("blog.editor.actions.preview")}
            </Button>
            {!isNew && (
              <Button
                variant="outline"
                disabled={isBusy}
                onClick={() => setDeleteConfirmOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("blog.editor.actions.delete")}
              </Button>
            )}
            {isPublished ? (
              <>
                <Button
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => unpublishMutation.mutate()}
                >
                  <EyeOff className="mr-2 h-4 w-4" />
                  {t("blog.editor.actions.unpublish")}
                </Button>
                <Button
                  disabled={!canSubmit || isBusy}
                  onClick={() => saveMutation.mutate(form)}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t("blog.editor.actions.update")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  disabled={!canSubmit || isBusy}
                  onClick={() => saveMutation.mutate(form)}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t("blog.editor.actions.saveDraft")}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button disabled={!canSubmit || isBusy}>
                      {t("blog.editor.actions.publishMenu")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setPublishConfirmOpen(true)}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      {t("blog.editor.actions.publishNow")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => scheduleMutation.mutate()}
                      disabled={!form.scheduledFor}
                    >
                      <CalendarClock className="mr-2 h-4 w-4" />
                      {t("blog.editor.actions.scheduleNow")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="space-y-1">
            <Label htmlFor="blog-title">{t("blog.editor.fields.title")}</Label>
            <Input
              id="blog-title"
              value={form.title}
              onChange={handleTitleChange}
              placeholder={t("blog.editor.fields.titlePlaceholder")}
              className="text-lg"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="blog-slug">{t("blog.editor.fields.slug")}</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">/blog/</span>
              <Input
                id="blog-slug"
                value={form.slug}
                onChange={handleSlugChange}
                placeholder={t("blog.editor.fields.slugPlaceholder")}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="blog-excerpt">
              {t("blog.editor.fields.excerpt")}
            </Label>
            <Textarea
              id="blog-excerpt"
              value={form.excerpt}
              onChange={(e) => updateField("excerpt", e.target.value)}
              placeholder={t("blog.editor.fields.excerptPlaceholder")}
              rows={2}
              maxLength={400}
            />
          </div>

          <div className="space-y-1">
            <Label>{t("blog.editor.fields.content")}</Label>
            <RichTextEditor
              value={form.content}
              onChange={(v) => updateField("content", v)}
              placeholder={t("blog.editor.fields.contentPlaceholder")}
            />
          </div>
        </div>

        <aside className="space-y-6 lg:col-span-1">
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold">
              {t("blog.editor.sections.cover")}
            </h3>
            {form.coverImageUrl ? (
              <div className="relative overflow-hidden rounded-md border">
                {coverLoadError ? (
                  <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-muted p-4 text-center text-xs text-muted-foreground">
                    <ImageIcon className="h-6 w-6" />
                    <p className="font-medium">
                      {t("blog.editor.fields.coverLoadError")}
                    </p>
                    <a
                      href={form.coverImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all text-primary underline"
                    >
                      {form.coverImageUrl}
                    </a>
                  </div>
                ) : (
                  <img
                    src={form.coverImageUrl}
                    alt={form.coverImageAlt || ""}
                    className="aspect-video w-full object-cover"
                    onError={() => {
                      console.warn(
                        "[BlogEditor] cover image failed to load:",
                        form.coverImageUrl,
                      );
                      setCoverLoadError(true);
                    }}
                  />
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 top-2 h-7 w-7"
                  onClick={() => updateField("coverImageUrl", "")}
                  aria-label={t("blog.editor.fields.coverRemove")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover || autoCoverPending}
                className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 text-sm text-muted-foreground transition hover:bg-muted/50"
              >
                {uploadingCover ? (
                  <LoadingLogo size="sm" />
                ) : autoCoverPending ? (
                  <>
                    <Sparkles className="h-6 w-6 animate-pulse" />
                    <span>{t("blog.editor.fields.coverGenerating")}</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="h-6 w-6" />
                    <span>{t("blog.editor.fields.coverEmpty")}</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleCoverChange}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
              >
                <Upload className="mr-2 h-4 w-4" />
                {form.coverImageUrl
                  ? t("blog.editor.fields.coverReplace")
                  : t("blog.editor.fields.coverUpload")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setCoverDialogOpen(true)}
                disabled={uploadingCover}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {t("blog.editor.fields.coverGenerate")}
              </Button>
            </div>
            {form.coverImageUrl && (
              <div className="space-y-1">
                <Label htmlFor="blog-cover-alt" className="text-xs">
                  {t("blog.editor.fields.coverAlt")}
                </Label>
                <Input
                  id="blog-cover-alt"
                  value={form.coverImageAlt}
                  onChange={(e) =>
                    updateField("coverImageAlt", e.target.value)
                  }
                />
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold">
              {t("blog.editor.sections.tags")}
            </h3>
            <Input
              value={form.tagsInput}
              onChange={(e) => updateField("tagsInput", e.target.value)}
              placeholder={t("blog.editor.fields.tagsPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">
              {t("blog.editor.fields.tagsHelp")}
            </p>
          </section>

          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold">
              {t("blog.editor.sections.schedule")}
            </h3>
            <Input
              type="datetime-local"
              value={form.scheduledFor}
              onChange={(e) => updateField("scheduledFor", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("blog.editor.fields.scheduleHelp")}
            </p>
          </section>

          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold">
              {t("blog.editor.sections.seo")}
            </h3>
            <div className="space-y-1">
              <Label htmlFor="blog-seo-title" className="text-xs">
                {t("blog.editor.fields.seoTitle")}
              </Label>
              <Input
                id="blog-seo-title"
                value={form.seoTitle}
                onChange={(e) => updateField("seoTitle", e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="blog-seo-description" className="text-xs">
                {t("blog.editor.fields.seoDescription")}
              </Label>
              <Textarea
                id="blog-seo-description"
                value={form.seoDescription}
                onChange={(e) =>
                  updateField("seoDescription", e.target.value)
                }
                rows={2}
                maxLength={400}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="blog-og-image" className="text-xs">
                {t("blog.editor.fields.ogImage")}
              </Label>
              <Input
                id="blog-og-image"
                value={form.ogImageUrl}
                onChange={(e) => updateField("ogImageUrl", e.target.value)}
                placeholder="https://…"
              />
            </div>
          </section>
        </aside>
      </div>
      <span className="sr-only" aria-live="polite">
        {i18n.language}
      </span>

      <GenerateDraftDialog
        open={draftDialogOpen}
        onOpenChange={setDraftDialogOpen}
        hasExistingContent={hasContent}
        hasExistingCover={form.coverImageUrl.length > 0}
        onGenerated={(draft, opts) => {
          setForm((prev) => ({
            ...prev,
            title: draft.title,
            slug: prev.slugTouched ? prev.slug : slugify(draft.title),
            excerpt: draft.excerpt,
            content: { html: draft.contentHtml, json: {} },
            tagsInput: draft.suggestedTags.join(", "),
            seoTitle: draft.seoTitle,
            seoDescription: draft.seoDescription,
          }));
          if (opts.autoCover) {
            setAutoCoverPending(true);
            const coverToastId = toast.loading(
              t("blog.editor.ai.toast.generatingCover"),
            );
            generateBlogCoverImage({
              prompt: draft.title,
              style: "photographic",
            })
              .then(({ url }) => {
                setForm((prev) => ({
                  ...prev,
                  coverImageUrl: url,
                  // The cover doubles as the OG image whenever the editor
                  // hasn't set an explicit one. Lets the post ship with a
                  // valid social card straight after AI generation.
                  ogImageUrl: prev.ogImageUrl.trim() ? prev.ogImageUrl : url,
                  coverImageAlt: prev.coverImageAlt.trim()
                    ? prev.coverImageAlt
                    : draft.title,
                }));
                toast.success(t("blog.editor.ai.toast.coverReady"), {
                  id: coverToastId,
                });
              })
              .catch(() => {
                toast.error(t("blog.editor.ai.toast.coverError"), {
                  id: coverToastId,
                });
              })
              .finally(() => {
                setAutoCoverPending(false);
              });
          }
        }}
      />

      <BlogPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        data={{
          title: form.title,
          excerpt: form.excerpt,
          contentHtml: form.content.html,
          coverImageUrl: form.coverImageUrl,
          coverImageAlt: form.coverImageAlt,
          tags: parseTagsInput(form.tagsInput),
        }}
      />

      <ConfirmationDialog
        open={publishConfirmOpen}
        onOpenChange={setPublishConfirmOpen}
        title={t("blog.editor.publishDialog.title")}
        description={t("blog.editor.publishDialog.description")}
        confirmText={t("blog.editor.publishDialog.confirm")}
        cancelText={t("blog.editor.publishDialog.cancel")}
        isLoading={publishMutation.isPending}
        onConfirm={() => {
          setPublishConfirmOpen(false);
          publishMutation.mutate();
        }}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t("blog.editor.deleteDialog.title")}
        description={t("blog.editor.deleteDialog.description", {
          title: form.title || t("blog.editor.preview.untitled"),
        })}
        confirmText={t("blog.editor.deleteDialog.confirm")}
        cancelText={t("blog.editor.deleteDialog.cancel")}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />

      <GenerateCoverImageDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        defaultPrompt={form.title.trim() || form.excerpt.trim()}
        onGenerated={(url) => {
          setForm((prev) => ({ ...prev, coverImageUrl: url }));
        }}
      />
    </div>
  );
}
